import { MembershipType } from "~/types/membership";
import { IBasketProduct, IProduct } from "~/types/product";
// import { IBasketSummationProps } from "~/components/basket-summation/basket-summation";
import { IProductTileProps } from "~/components/product-tile/product-tile";

enum EventTypes {
    SIGNUP = "eec.signup",
    PURCHASE = "eec.purchase",
    CHECKOUT = "eec.checkout",
    ADD_TO_BASKET = "eec.add",
    REMOVE_FROM_BASKET = "eec.remove",
    CLICK = "eec.impressionClick",
    DETAIL_VIEW = "eec.detail",
    IMPRESSION_VIEW = "eec.impressionView",
    PROMO_VIEW = "eec.promotionView",
    PROMO_CLICK = "eec.promotionClick",
    VOUCHER = "eec.voucher"
}

enum CurrencyCodes {
    DKK = "DKK"
}

enum TrackingDimensions {
    CAMPAIGN_ID = "dimension2",
    MEMBERSHIP_TYPE = "dimension3",
    PAYMENT_PERIOD = "dimension4",
    DISCOUNT_SPLASH = "dimension5"
}

interface ITrackingProduct {
    name?: string;
    id?: string;
    price?: number;
    quantity?: number;
    [TrackingDimensions.MEMBERSHIP_TYPE]?: MembershipType | string;
    [TrackingDimensions.PAYMENT_PERIOD]?: string;
    [TrackingDimensions.DISCOUNT_SPLASH]?: string;
}

interface IProductImpression extends ITrackingProduct {
    list?: string;
    position: number | undefined;
}

interface ICheckoutActionField {
    step: string;
    [TrackingDimensions.CAMPAIGN_ID]?: string;
    option?: string;
    coupon?: string;
}

interface IEcommerceTrackingEvent<T> {
    event: EventTypes;
    ecommerce: T & { currencyCode?: CurrencyCodes };
}

interface ITrackingEvent {
    event: "trackEvent";
    eventData: {
        category: string;
        action: string;
        label: string;
    };
}

const mapProductsToTrackingProducts = (
    { title, primaryPriceNumber, productId, quantityNumber, discounts }: IBasketProduct,
    membershipType?: MembershipType,
    paymentPeriod?: string
): ITrackingProduct => {
    const product: ITrackingProduct = {
        name: title,
        price: primaryPriceNumber,
        quantity: quantityNumber || 1,
        [TrackingDimensions.MEMBERSHIP_TYPE]: membershipType || "",
        [TrackingDimensions.PAYMENT_PERIOD]: paymentPeriod || "",
        [TrackingDimensions.DISCOUNT_SPLASH]: discounts?.map(discount => discount.label).join(" | ") || ""
    };

    if (productId) {
        product.id = productId;
    }

    return product;
};

export interface ISignupTrackingParams {
    campaignId?: string;
    membershipType?: MembershipType;
    currentStepIndex: number;
    paymentPeriod?: string;
    miniBasketProducts?: IBasketProduct[];
}

interface ISignupTrackingEvent
    extends IEcommerceTrackingEvent<{
        checkout: {
            actionField: ICheckoutActionField;
            products: ITrackingProduct[];
        };
    }> {}

const generateSignupTrackingEvent = ({
    membershipType,
    campaignId,
    miniBasketProducts,
    currentStepIndex,
    paymentPeriod
}: ISignupTrackingParams): ISignupTrackingEvent => {
    const actionField: ICheckoutActionField = {
        step: (currentStepIndex + 1).toString(),
        option: currentStepIndex > 0 ? paymentPeriod : ""
    };

    actionField[TrackingDimensions.CAMPAIGN_ID] = campaignId || "";

    const products: ITrackingProduct[] = miniBasketProducts
        ? miniBasketProducts.map(product =>
              mapProductsToTrackingProducts(product, membershipType, paymentPeriod)
          )
        : [];

    const signupTrackingEvent = {
        event: EventTypes.SIGNUP,
        ecommerce: {
            checkout: {
                actionField,
                products
            }
        }
    };

    return signupTrackingEvent;
};

interface ICheckoutTrackingParams {
    products?: IBasketProduct[];
    paymentMethod?: string;
    step: number;
}

interface ICheckoutTrackingEvent
    extends IEcommerceTrackingEvent<{
        checkout: {
            actionField: ICheckoutActionField;
            products: ITrackingProduct[];
        };
    }> {}

const generateCheckoutTrackingEvent = ({
    products,
    step,
    paymentMethod
}: ICheckoutTrackingParams): ICheckoutTrackingEvent => {
    const actionField: ICheckoutActionField = {
        step: `${step}`
    };

    if (paymentMethod) {
        actionField.option = paymentMethod;
    }

    const mappedProducts: ITrackingProduct[] = products
        ? products.map(product => mapProductsToTrackingProducts(product))
        : [];

    return {
        event: EventTypes.CHECKOUT,
        ecommerce: {
            checkout: {
                actionField,
                products: mappedProducts
            }
        }
    };
};

interface IPurchaseTrackingParams {
    membershipType?: MembershipType;
    miniBasketProducts?: IBasketProduct[];
    // miniBasketSummation?: IBasketSummationProps;
    orderId?: string;
    campaignId?: string;
}

interface IPurchaseActionField {
    [TrackingDimensions.CAMPAIGN_ID]?: string;
    coupon?: string;
    revenue?: number;
    shipping?: number;
    tax?: number;
    id?: string;
}

interface IPurchaseTrackingEvent
    extends IEcommerceTrackingEvent<{
        purchase: {
            actionField: IPurchaseActionField;
            products: ITrackingProduct[];
        };
    }> {}

const generatePurchaseTrackingEvent = ({
    membershipType,
    miniBasketProducts,
    // miniBasketSummation,
    orderId,
    campaignId
}: IPurchaseTrackingParams): IPurchaseTrackingEvent => {
    const actionField: IPurchaseActionField = {};

    if (orderId) {
        actionField.id = orderId;
    }

    actionField[TrackingDimensions.CAMPAIGN_ID] = campaignId || "";

    // if (miniBasketSummation) {
    //     actionField.tax = miniBasketSummation.vatNumber;
    //     actionField.revenue = miniBasketSummation.totalPriceNumber;

    //     if (miniBasketSummation.shippingFee) {
    //         actionField.shipping = miniBasketSummation.shippingFeeNumber;
    //     }
    //     if (miniBasketSummation.voucherDiscount) {
    //         actionField.coupon = miniBasketSummation.voucherDiscount;
    //     }
    // }

    const products: ITrackingProduct[] = miniBasketProducts
        ? miniBasketProducts.map(product => mapProductsToTrackingProducts(product, membershipType))
        : [];

    const purchaseTrackingEvent = {
        event: EventTypes.PURCHASE,
        ecommerce: {
            currencyCode: CurrencyCodes.DKK,
            purchase: {
                actionField,
                products
            }
        }
    };

    return purchaseTrackingEvent;
};

interface IClickActionField {
    list: string;
}

interface IClickTrackingParams
    extends Pick<IProductTileProps, "trackingList" | "trackingPosition" | "productId" | "title"> {}

interface IClickTrackingEvent
    extends IEcommerceTrackingEvent<{
        click: {
            actionField: IClickActionField;
            products: IProductImpression[];
        };
    }> {}

const generateClickTrackingEvent = ({
    trackingList,
    trackingPosition,
    productId,
    title
}: IClickTrackingParams): IClickTrackingEvent => {
    return {
        event: EventTypes.CLICK,
        ecommerce: {
            click: {
                actionField: {
                    list: trackingList
                },
                products: [
                    {
                        id: productId,
                        name: title,
                        position: trackingPosition
                    }
                ]
            }
        }
    };
};

interface IDetailActionField {
    list: string;
}

interface IDetailViewTrackingParams extends Pick<IProduct, "title" | "productId"> {
    trackingList: string;
}

interface IDetailViewTrackingEvent
    extends IEcommerceTrackingEvent<{
        detail: {
            actionField: IDetailActionField;
            products: ITrackingProduct[];
        };
    }> {}

const generateDetailViewTrackingEvent = ({
    title,
    productId,
    trackingList
}: IDetailViewTrackingParams): IDetailViewTrackingEvent => {
    return {
        event: EventTypes.DETAIL_VIEW,
        ecommerce: {
            detail: {
                actionField: {
                    list: trackingList
                },
                products: [
                    {
                        id: productId,
                        name: title
                    }
                ]
            }
        }
    };
};

interface IBasketTrackingParams {
    products?: IBasketProduct[];
    trackingList: string;
}

interface IAddToBasketTrackingEvent
    extends IEcommerceTrackingEvent<{
        add: {
            actionField: { list: string };
            products: ITrackingProduct[] | undefined;
        };
    }> {}

const generateAddToBasketTrackingEvent = ({
    products,
    trackingList
}: IBasketTrackingParams): IAddToBasketTrackingEvent => {
    const basketProducts = products && products.map(product => mapProductsToTrackingProducts(product));

    return {
        event: EventTypes.ADD_TO_BASKET,
        ecommerce: {
            add: {
                actionField: { list: trackingList },
                products: basketProducts
            }
        }
    };
};

interface IRemoveFromBasketTrackingEvent
    extends IEcommerceTrackingEvent<{
        remove: {
            actionField: { list: string };
            products: ITrackingProduct[] | undefined;
        };
    }> {}

const generateRemoveFromBasketTrackingEvent = ({
    products,
    trackingList
}: IBasketTrackingParams): IRemoveFromBasketTrackingEvent => {
    const basketProducts = products && products.map(product => mapProductsToTrackingProducts(product));

    return {
        event: EventTypes.REMOVE_FROM_BASKET,
        ecommerce: {
            remove: {
                actionField: { list: trackingList },
                products: basketProducts
            }
        }
    };
};

interface IVoucherTrackingParams {
    // summation?: IBasketSummationProps;
    caller: string;
    action: string;
}
interface IVoucherTrackingEvent
    extends IEcommerceTrackingEvent<{
        // summation?: IBasketSummationProps;
        caller: string;
        action: string;
    }> {}

const generateVoucherTrackingEvent = (props: IVoucherTrackingParams): IVoucherTrackingEvent => {
    return {
        event: EventTypes.VOUCHER,
        ecommerce: {
            // summation: props.summation,
            caller: props.caller,
            action: props.action
        }
    };
};

interface IImpressionViewTrackingParams
    extends Required<Pick<IProductTileProps, "trackingList" | "trackingPosition" | "productId" | "title">> {}

interface IImpressionViewTrackingEvent
    extends IEcommerceTrackingEvent<{
        impressions: IProductImpression[];
    }> {}

const generateImpressionViewTrackingEvent = (
    products: IImpressionViewTrackingParams[]
): IImpressionViewTrackingEvent => {
    return {
        event: EventTypes.IMPRESSION_VIEW,
        ecommerce: {
            impressions: products.map(product => {
                return {
                    list: product.trackingList,
                    position: product.trackingPosition,
                    id: product.productId,
                    name: product.title
                };
            })
        }
    };
};

interface IPromoViewTrackingEvent
    extends IEcommerceTrackingEvent<{
        promoView: {
            promotions: Array<{
                id: string;
                name: string;
                creative: string;
                position: string;
            }>;
        };
    }> {}

const generatePromoViewTrackingEvent = (): IPromoViewTrackingEvent => {
    return {
        event: EventTypes.PROMO_VIEW,
        ecommerce: {
            promoView: {
                promotions: [
                    {
                        id: "",
                        name: "Splash",
                        creative: "",
                        position: ""
                    }
                ]
            }
        }
    };
};

interface IPromoClickTrackingEvent
    extends IEcommerceTrackingEvent<{
        promoClick: {
            promotions: Array<{
                id: string;
                name: string;
                creative: string;
                position: string;
            }>;
        };
    }> {}

const generatePromoClickTrackingEvent = (): IPromoClickTrackingEvent => {
    return {
        event: EventTypes.PROMO_CLICK,
        ecommerce: {
            promoClick: {
                promotions: [
                    {
                        id: "",
                        name: "Splash",
                        creative: "",
                        position: ""
                    }
                ]
            }
        }
    };
};

const ensureDataLayer = () => {
    if (typeof window === "undefined") {
        return false;
    }

    window.dataLayer = window.dataLayer || [];
    return true;
};

const pushEcommerenceTrackingEvent = (trackingEvent: IEcommerceTrackingEvent<unknown>) => {
    if (ensureDataLayer()) window.dataLayer.push(trackingEvent);
};

export const pushTrackingEvent = (trackingEvent: ITrackingEvent) => {
    if (ensureDataLayer()) window.dataLayer.push(trackingEvent);
};

export const pushAddToBasketTrackingEvent = (props: IBasketTrackingParams) =>
    pushEcommerenceTrackingEvent(generateAddToBasketTrackingEvent(props));

export const pushRemoveFromBasketTrackingEvent = (props: IBasketTrackingParams) =>
    pushEcommerenceTrackingEvent(generateRemoveFromBasketTrackingEvent(props));

export const pushSignupTrackingEvent = (props: ISignupTrackingParams) =>
    pushEcommerenceTrackingEvent(generateSignupTrackingEvent(props));

export const pushCheckoutTrackingEvent = (props: ICheckoutTrackingParams) =>
    pushEcommerenceTrackingEvent(generateCheckoutTrackingEvent(props));

export const pushPurchaseTrackingEvent = (props: IPurchaseTrackingParams) =>
    pushEcommerenceTrackingEvent(generatePurchaseTrackingEvent(props));

export const pushClickTrackingEvent = (props: IClickTrackingParams) =>
    pushEcommerenceTrackingEvent(generateClickTrackingEvent(props));

export const pushDetailViewTrackingEvent = (props: IDetailViewTrackingParams) =>
    pushEcommerenceTrackingEvent(generateDetailViewTrackingEvent(props));

export const pushImpressionViewTrackingEvent = (props: IImpressionViewTrackingParams[]) =>
    pushEcommerenceTrackingEvent(generateImpressionViewTrackingEvent(props));

export const pushPromoViewTrackingEvent = () =>
    pushEcommerenceTrackingEvent(generatePromoViewTrackingEvent());

export const pushPromoClickTrackingEvent = () =>
    pushEcommerenceTrackingEvent(generatePromoClickTrackingEvent());

export const pushVoucherTrackingEvent = (props: IVoucherTrackingParams) =>
    pushEcommerenceTrackingEvent(generateVoucherTrackingEvent(props));
